import React from 'react';
import { Star, Quote } from 'lucide-react';
import { testimonials } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { FadeIn, StaggerContainer } from './AnimatedComponents';

const Testimonials = () => {
  const { t } = useLanguage();
  
  return (
    <section className="py-24 bg-stone-900 relative overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img
          src="/images/restaurant/trattoria_da_daniele_003.jpg"
          alt="Restaurant ambiance"
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-stone-900 via-stone-900/95 to-stone-900" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <FadeIn className="text-center mb-12">
          <p className="text-amber-500 uppercase tracking-[0.3em] text-sm mb-4">
            {t('testimonials.subtitle')}
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-white">
            {t('testimonials.title')}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-amber-600 to-amber-400 mx-auto mt-6" />
          <p className="text-amber-100/60 mt-4 text-sm">
            4.8/5 {t('testimonials.rating')}
          </p>
        </FadeIn>

        {/* Testimonials Grid */}
        <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" staggerDelay={100}>
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              className="group bg-stone-950/50 backdrop-blur-sm border border-stone-800 hover:border-amber-700/50 rounded-2xl p-6 transition-all duration-500 hover:-translate-y-3 hover:shadow-xl hover:shadow-amber-900/20 relative"
            >
              {/* Quote Icon */}
              <div className="absolute -top-3 -left-3 w-8 h-8 bg-gradient-to-br from-amber-600 to-amber-800 rounded-full flex items-center justify-center group-hover:scale-110 group-hover:rotate-12 transition-all duration-300">
                <Quote className="w-4 h-4 text-white" />
              </div>

              {/* Stars */}
              <div className="flex gap-0.5 mb-3">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star 
                    key={i} 
                    className="w-4 h-4 fill-amber-500 text-amber-500 transition-transform duration-300"
                    style={{ transitionDelay: `${i * 50}ms` }}
                  />
                ))}
              </div>

              {/* Text */}
              <p className="text-amber-100/80 text-sm leading-relaxed mb-4 italic line-clamp-4">
                "{testimonial.text}"
              </p>

              {/* Author */}
              <p className="text-amber-400 font-semibold text-sm group-hover:text-amber-300 transition-colors">
                {testimonial.name}
              </p>
            </div>
          ))}
        </StaggerContainer>

        {/* Lunchgate Badge */}
        <FadeIn delay={400} className="text-center mt-10">
          <a 
            href="https://www.lunchgate.ch/restaurant/trattoria-pizzeria-da-daniele"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-amber-100/50 hover:text-amber-400 transition-all duration-300 hover:scale-105 border-animate"
          >
            {t('testimonials.source')}
          </a>
        </FadeIn>
      </div>
    </section>
  );
};

export default Testimonials;
